import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, catchError, filter, map, Observable, of, switchMap, take, timeout } from 'rxjs';
import { UserInfo } from '../../generated/auth/models/Sasfin/Wealth/Online/Auth/Models/user-info';
import { Permission } from '../../models/permission.model';
import { AuthService } from '../../services/auth.service';
import { refreshUserInformation } from '../../store/auth/auth.actions';
import {
  selectRefreshingUserInformationLoading,
  selectUserInformation,
} from '../../store/auth/auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {
  private store = inject(Store);
  private router = inject(Router);
  private authService = inject(AuthService);

  constructor() {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const requiredPermissions: Permission[] = route.data['permissions'] || [];

    if (!requiredPermissions || requiredPermissions.length === 0) {
      return of(true);
    }

    if (!this.authService.getTokenValue()) {
      this.authService.navigateToLogin();
      return of(false);
    }

    return this.getUserInformation$().pipe(
      map((userInfo) => {
        const userRoles = userInfo?.roles || [];
        const hasPermission = requiredPermissions.some((permission) =>
          userRoles.includes(permission),
        );

        if (!hasPermission) {
          this.router.navigate(['/private/access-denied']);
          return false;
        }

        return true;
      }),
      catchError((error) => {
        console.error('PermissionGuard error:', error);
        this.router.navigate(['/private/access-denied']);
        return of(false);
      }),
    );
  }

  private getUserInformation$(): Observable<UserInfo> {
    return combineLatest([
      this.store.select(selectUserInformation),
      this.store.select(selectRefreshingUserInformationLoading),
    ]).pipe(
      take(1),
      switchMap(([userInfo, isLoading]) => {
        if (userInfo && !isLoading) {
          return of(userInfo);
        }

        if (!isLoading) {
          this.store.dispatch(refreshUserInformation());
        }

        return combineLatest([
          this.store.select(selectUserInformation),
          this.store.select(selectRefreshingUserInformationLoading),
        ]).pipe(
          filter(([info, loading]) => !loading && !!info),
          map(([info]) => info),
          take(1),
          timeout(10000),
        );
      }),
    );
  }
}
